// Forked from: https://github.com/sidorares/json-bigint/blob/master/lib/parse.js
// ------------
// regexpxs extracted from
// (c) BSD-3-Clause
// https://github.com/fastify/secure-json-parse/graphs/contributors and https://github.com/hapijs/bourne/graphs/contributors
const suspectProtoRx = /(?:_|\\u005[Ff])(?:_|\\u005[Ff])(?:p|\\u0070)(?:r|\\u0072)(?:o|\\u006[Ff])(?:t|\\u0074)(?:o|\\u006[Ff])(?:_|\\u005[Ff])(?:_|\\u005[Ff])/;
const suspectConstructorRx = /(?:c|\\u0063)(?:o|\\u006[Ff])(?:n|\\u006[Ee])(?:s|\\u0073)(?:t|\\u0074)(?:r|\\u0072)(?:u|\\u0075)(?:c|\\u0063)(?:t|\\u0074)(?:o|\\u006[Ff])(?:r|\\u0072)/;
export default function (reviver) {
    let at, // The index of the current character
    ch; // The current character
    const escapee = {
        '"': '"',
        '\\': '\\',
        '/': '/',
        b: '\b',
        f: '\f',
        n: '\n',
        r: '\r',
        t: '\t',
    };
    const allowedWhiteSpaces = new Set([
        ' ',
        '\n',
        '\t',
        '\r',
        '\v',
        '\u00a0',
        '\u1680',
        '\u2000',
        '\u200a',
        '\u2028',
        '\u2029',
        '\u202f',
        '\u205f',
        '\u3000',
        '\ufeff',
    ]);
    let text;
    const errorFn = function (m) {
        // Call error when something is wrong.
        throw {
            name: 'SyntaxError',
            message: m,
            at,
            text,
        };
    }, nextFn = function (c) {
        // If a c parameter is provided, verify that it matches the current character.
        if (c && c !== ch) {
            errorFn(`Expected '${c}'  instead of '${ch}'`);
        }
        // Get the next character. When there are no more characters,
        // return the empty string.
        ch = text.charAt(at);
        at += 1;
        return ch;
    }, numberFn = function (key) {
        // Parse a number value.
        let string = '';
        if (ch === '-') {
            string = '-';
            nextFn('-');
        }
        if (ch === '0' && text.charAt(at) >= '0' && text.charAt(at) <= '9')
            errorFn('Bad number');
        while (ch >= '0' && ch <= '9') {
            string += ch;
            nextFn();
        }
        if (ch === '.') {
            string += '.';
            if (text.charAt(at - 2) === '-')
                errorFn('Bad number');
            while (nextFn() && ch >= '0' && ch <= '9') {
                string += ch;
            }
            if (string.endsWith('.'))
                errorFn('Bad number');
        }
        if (ch === 'e' || ch === 'E') {
            string += ch;
            nextFn();
            if (ch === '-' || ch === '+') {
                string += ch;
                nextFn();
            }
            while (ch >= '0' && ch <= '9') {
                string += ch;
                nextFn();
            }
        }
        if (ch !== undefined && ch === '\x00')
            errorFn('Bad number');
        const number = +string;
        if (!isFinite(number)) {
            errorFn('Bad number');
        }
        else {
            if (Number.isSafeInteger(number) || !Number.isInteger(number))
                return number;
            // Number with fractional part should be treated as numberFn(double) including big integers in scientific notation, i.e 1.79e+308
            else
                return reviver(key, number, { source: string });
        }
    }, stringFn = function () {
        // Parse a string value.
        let hex, i, string = '', uffff;
        // When parsing for string values, we must look for " and \ characters.
        if (ch === '"') {
            let startAt = at;
            while (nextFn()) {
                if (ch < '\x20')
                    break;
                if (ch === '"') {
                    if (at - 1 > startAt)
                        string += text.substring(startAt, at - 1);
                    nextFn();
                    return string;
                }
                if (ch === '\\') {
                    if (at - 1 > startAt)
                        string += text.substring(startAt, at - 1);
                    nextFn();
                    if (ch === 'u') {
                        uffff = 0;
                        for (i = 0; i < 4; i += 1) {
                            hex = parseInt(nextFn(), 16);
                            if (isNaN(hex))
                                errorFn('Bad string');
                            if (!isFinite(hex)) {
                                break;
                            }
                            uffff = uffff * 16 + hex;
                        }
                        string += String.fromCharCode(uffff);
                    }
                    else if (typeof escapee[ch] === 'string') {
                        string += escapee[ch];
                    }
                    else {
                        break;
                    }
                    startAt = at;
                }
            }
        }
        errorFn('Bad string');
    }, whiteFn = function () {
        // Skip whitespace.
        while (ch && allowedWhiteSpaces.has(ch)) {
            nextFn();
        }
    }, wordFn = function () {
        // true, false, or null.
        switch (ch) {
            case 't':
                nextFn('t');
                nextFn('r');
                nextFn('u');
                nextFn('e');
                return true;
            case 'f':
                nextFn('f');
                nextFn('a');
                nextFn('l');
                nextFn('s');
                nextFn('e');
                return false;
            case 'n':
                nextFn('n');
                nextFn('u');
                nextFn('l');
                nextFn('l');
                return null;
        }
        errorFn(`Unexpected '${ch}'`);
    }, arrayFn = function (key) {
        // Parse an array value.
        const array = [];
        if (ch === '[') {
            nextFn('[');
            whiteFn();
            if (ch === ']') {
                nextFn(']');
                return array; // empty array
            }
            while (ch) {
                array.push(value(key));
                whiteFn();
                if (ch === ']') {
                    nextFn(']');
                    return array;
                }
                nextFn(',');
                whiteFn();
            }
        }
        errorFn('Bad array');
    }, objectFn = function () {
        // Parse an object value.
        let key;
        const object = {};
        if (ch === '{') {
            nextFn('{');
            whiteFn();
            if (ch === '}') {
                nextFn('}');
                return object; // empty object
            }
            while (ch) {
                key = stringFn() ?? '';
                whiteFn();
                nextFn(':');
                if (suspectProtoRx.test(key) === true) {
                    errorFn('Object contains forbidden prototype property');
                }
                else if (suspectConstructorRx.test(key) === true) {
                    errorFn('Object contains forbidden constructor property');
                }
                else {
                    object[key] = value(key);
                }
                whiteFn();
                if (ch === '}') {
                    nextFn('}');
                    return object;
                }
                nextFn(',');
                whiteFn();
            }
        }
        errorFn('Bad object');
    };
    const value = function (key) {
        // Parse a JSON value. It could be an object, an array, a string, a number,
        // or a word.
        whiteFn();
        switch (ch) {
            case '{':
                return objectFn();
            case '[':
                return arrayFn(key);
            case '"':
                return stringFn();
            case '-':
                return numberFn(key);
            default:
                return ch >= '0' && ch <= '9' ? numberFn(key) : wordFn();
        }
    };
    return function (source) {
        text = String(source);
        at = 0;
        ch = ' ';
        const result = value('');
        whiteFn();
        if (ch) {
            errorFn('Syntax error');
        }
        return result;
    };
}
//# sourceMappingURL=create-parse.js.map